"use client";

import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { NextIntlClientProvider } from "next-intl";
import { Locale, defaultLocale } from "@/lib/i18n/config";
import { getLocaleFromCookie, setLocaleCookie } from "@/lib/i18n/locale-cookie";

import enMessages from "../../../messages/en.json";
import ptBRMessages from "../../../messages/pt-BR.json";
import esMessages from "../../../messages/es.json";

const messagesMap: Record<Locale, typeof enMessages> = {
  en: enMessages,
  "pt-BR": ptBRMessages,
  es: esMessages,
};

interface UserLocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const UserLocaleContext = createContext<UserLocaleContextValue>({
  locale: defaultLocale,
  setLocale: () => {},
});

export function useUserLocale() {
  return useContext(UserLocaleContext);
}

interface UserLocaleProviderProps {
  children: ReactNode;
}

export function UserLocaleProvider({ children }: UserLocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);

  // Read locale from cookie on mount
  useEffect(() => {
    const cookieLocale = getLocaleFromCookie();
    if (cookieLocale !== locale) {
      setLocaleState(cookieLocale);
    }
    // Sync document lang on mount
    document.documentElement.lang = cookieLocale;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    setLocaleCookie(newLocale);
  };

  const messages = messagesMap[locale] || messagesMap[defaultLocale];

  return (
    <UserLocaleContext.Provider value={{ locale, setLocale }}>
      <NextIntlClientProvider
        locale={locale}
        messages={messages}
        timeZone="UTC"
      >
        {children}
      </NextIntlClientProvider>
    </UserLocaleContext.Provider>
  );
}
